/**
 * Utility functions for managing user preferences in localStorage
 */

// Define interfaces for user data
export interface TriedFlavor {
  id: string;
  date: string;
}

export interface NotificationSettings {
  newFlavors: boolean;
  emailNotifications: boolean;
  email: string;
}

// Storage keys
const STORAGE_KEYS = {
  FAVORITES: 'geekBarPulse_favorites',
  TRIED_FLAVORS: 'geekBarPulse_triedFlavors',
  USER_NOTES: 'geekBarPulse_userNotes',
  USER_RATINGS: 'geekBarPulse_userRatings',
  THEME: 'geekBarPulse_theme',
  NOTIFICATION_SETTINGS: 'geekBarPulse_notificationSettings'
};

/**
 * Save favorites to localStorage
 * @param favorites - Array of flavor IDs
 */
export const saveFavorites = (favorites: string[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(favorites));
  } catch (error) {
    console.error('Error saving favorites to localStorage:', error);
  }
};

/**
 * Load favorites from localStorage
 * @returns - Array of favorite flavor IDs
 */
export const loadFavorites = (): string[] => {
  try {
    const favorites = localStorage.getItem(STORAGE_KEYS.FAVORITES);
    return favorites ? JSON.parse(favorites) : [];
  } catch (error) {
    console.error('Error loading favorites from localStorage:', error);
    return [];
  }
};

/**
 * Add a flavor to favorites
 * @param flavorId - ID of the flavor to add
 * @returns - Updated array of favorite flavor IDs
 */
export const addToFavorites = (flavorId: string): string[] => {
  const favorites = loadFavorites();
  if (!favorites.includes(flavorId)) {
    const updatedFavorites = [...favorites, flavorId];
    saveFavorites(updatedFavorites);
    return updatedFavorites;
  }
  return favorites;
};

/**
 * Remove a flavor from favorites
 * @param flavorId - ID of the flavor to remove
 * @returns - Updated array of favorite flavor IDs
 */
export const removeFromFavorites = (flavorId: string): string[] => {
  const favorites = loadFavorites();
  const updatedFavorites = favorites.filter(id => id !== flavorId);
  saveFavorites(updatedFavorites);
  return updatedFavorites;
};

/**
 * Check if a flavor is in favorites
 * @param flavorId - ID of the flavor to check
 * @returns - Whether the flavor is in favorites
 */
export const isFavorite = (flavorId: string): boolean => {
  const favorites = loadFavorites();
  return favorites.includes(flavorId);
};

/**
 * Save tried flavors to localStorage
 * @param triedFlavors - Array of tried flavor objects
 */
export const saveTriedFlavors = (triedFlavors: TriedFlavor[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.TRIED_FLAVORS, JSON.stringify(triedFlavors));
  } catch (error) {
    console.error('Error saving tried flavors to localStorage:', error);
  }
};

/**
 * Load tried flavors from localStorage
 * @returns - Array of tried flavor objects
 */
export const loadTriedFlavors = (): TriedFlavor[] => {
  try {
    const triedFlavors = localStorage.getItem(STORAGE_KEYS.TRIED_FLAVORS);
    return triedFlavors ? JSON.parse(triedFlavors) : [];
  } catch (error) {
    console.error('Error loading tried flavors from localStorage:', error);
    return [];
  }
};

/**
 * Add a flavor to tried flavors
 * @param flavorId - ID of the flavor to add
 * @param triedDate - Date when the flavor was tried
 * @returns - Updated array of tried flavor objects
 */
export const addToTriedFlavors = (flavorId: string, triedDate: Date = new Date()): TriedFlavor[] => {
  const triedFlavors = loadTriedFlavors();
  if (!triedFlavors.some(item => item.id === flavorId)) {
    const updatedTriedFlavors = [
      ...triedFlavors,
      { id: flavorId, date: triedDate.toISOString() }
    ];
    saveTriedFlavors(updatedTriedFlavors);
    return updatedTriedFlavors;
  }
  return triedFlavors;
};

/**
 * Remove a flavor from tried flavors
 * @param flavorId - ID of the flavor to remove
 * @returns - Updated array of tried flavor objects
 */
export const removeFromTriedFlavors = (flavorId: string): TriedFlavor[] => {
  const triedFlavors = loadTriedFlavors();
  const updatedTriedFlavors = triedFlavors.filter(item => item.id !== flavorId);
  saveTriedFlavors(updatedTriedFlavors);
  return updatedTriedFlavors;
};

/**
 * Check if a flavor has been tried
 * @param flavorId - ID of the flavor to check
 * @returns - Whether the flavor has been tried
 */
export const hasTried = (flavorId: string): boolean => {
  const triedFlavors = loadTriedFlavors();
  return triedFlavors.some(item => item.id === flavorId);
};

/**
 * Save user notes to localStorage
 * @param notes - Object mapping flavor IDs to notes
 */
export const saveUserNotes = (notes: Record<string, string>): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_NOTES, JSON.stringify(notes));
  } catch (error) {
    console.error('Error saving user notes to localStorage:', error);
  }
};

/**
 * Load user notes from localStorage
 * @returns - Object mapping flavor IDs to notes
 */
export const loadUserNotes = (): Record<string, string> => {
  try {
    const notes = localStorage.getItem(STORAGE_KEYS.USER_NOTES);
    return notes ? JSON.parse(notes) : {};
  } catch (error) {
    console.error('Error loading user notes from localStorage:', error);
    return {};
  }
};

/**
 * Add or update a note for a flavor
 * @param flavorId - ID of the flavor
 * @param note - Note text
 * @returns - Updated notes object
 */
export const updateNote = (flavorId: string, note: string): Record<string, string> => {
  const notes = loadUserNotes();
  const updatedNotes = { ...notes, [flavorId]: note };
  saveUserNotes(updatedNotes);
  return updatedNotes;
};

/**
 * Get note for a flavor
 * @param flavorId - ID of the flavor
 * @returns - Note text or null if no note exists
 */
export const getNote = (flavorId: string): string | null => {
  const notes = loadUserNotes();
  return notes[flavorId] || null;
};

/**
 * Save user ratings to localStorage
 * @param ratings - Object mapping flavor IDs to ratings
 */
export const saveUserRatings = (ratings: Record<string, number>): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_RATINGS, JSON.stringify(ratings));
  } catch (error) {
    console.error('Error saving user ratings to localStorage:', error);
  }
};

/**
 * Load user ratings from localStorage
 * @returns - Object mapping flavor IDs to ratings
 */
export const loadUserRatings = (): Record<string, number> => {
  try {
    const ratings = localStorage.getItem(STORAGE_KEYS.USER_RATINGS);
    return ratings ? JSON.parse(ratings) : {};
  } catch (error) {
    console.error('Error loading user ratings from localStorage:', error);
    return {};
  }
};

/**
 * Rate a flavor
 * @param flavorId - ID of the flavor
 * @param rating - Rating value (1-5)
 * @returns - Updated ratings object
 */
export const rateFlavor = async (flavorId: string, rating: number): Promise<Record<string, number>> => {
  try {
    // First, save to local storage for immediate feedback
    const ratings = loadUserRatings();
    const updatedRatings = { ...ratings, [flavorId]: rating };
    saveUserRatings(updatedRatings);
    
    // Then, try to submit to GitHub (if available)
    try {
      // Dynamically import to avoid issues with SSR
      const { submitRating } = await import('./githubUtils');
      await submitRating(flavorId, rating);
    } catch (error) {
      console.error('Error submitting rating to GitHub:', error);
      // Continue with local storage only if GitHub submission fails
    }
    
    return updatedRatings;
  } catch (error) {
    console.error('Error rating flavor:', error);
    return {};
  }
};

/**
 * Get user rating for a flavor
 * @param flavorId - ID of the flavor
 * @returns - Rating value or null if no rating exists
 */
export const getUserRating = (flavorId: string): number | null => {
  // For user's own ratings, we still use localStorage
  const ratings = loadUserRatings();
  return ratings[flavorId] !== undefined ? ratings[flavorId] : null;
};

/**
 * Save theme preference to localStorage
 * @param theme - Theme name ('light' or 'dark')
 */
export const saveTheme = (theme: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
  } catch (error) {
    console.error('Error saving theme to localStorage:', error);
  }
};

/**
 * Load theme preference from localStorage
 * @returns - Theme name ('light' or 'dark')
 */
export const loadTheme = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEYS.THEME) || 'light';
  } catch (error) {
    console.error('Error loading theme from localStorage:', error);
    return 'light';
  }
};

/**
 * Save notification settings to localStorage
 * @param settings - Notification settings object
 */
export const saveNotificationSettings = (settings: NotificationSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.NOTIFICATION_SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving notification settings to localStorage:', error);
  }
};

/**
 * Load notification settings from localStorage
 * @returns - Notification settings object
 */
export const loadNotificationSettings = (): NotificationSettings => {
  try {
    const settings = localStorage.getItem(STORAGE_KEYS.NOTIFICATION_SETTINGS);
    return settings ? JSON.parse(settings) : {
      newFlavors: true,
      emailNotifications: false,
      email: ''
    };
  } catch (error) {
    console.error('Error loading notification settings from localStorage:', error);
    return {
      newFlavors: true,
      emailNotifications: false,
      email: ''
    };
  }
};

/**
 * Update notification settings
 * @param settings - Partial notification settings to update
 * @returns - Updated notification settings
 */
export const updateNotificationSettings = (settings: Partial<NotificationSettings>): NotificationSettings => {
  const currentSettings = loadNotificationSettings();
  const updatedSettings = { ...currentSettings, ...settings };
  saveNotificationSettings(updatedSettings);
  return updatedSettings;
};

/**
 * Clear all user data from localStorage
 */
export const clearAllUserData = (): void => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error clearing user data from localStorage:', error);
  }
};
